(function() {
  if (window.JomoShipmentSchedule) return;

  const DAY = 24 * 60 * 60 * 1000;
  const holidayYears = new Map();
  const torontoDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto', calendar: 'gregory', numberingSystem: 'latn',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
  });
  let configText;
  let config;
  let refreshTimer;
  let lastSignature;

  function utcDay(year, month, day) {
    return Date.UTC(year, month - 1, day) / DAY;
  }

  function dateKey(day) {
    return new Date(day * DAY).toISOString().slice(0, 10);
  }

  function weekday(day) {
    return new Date(day * DAY).getUTCDay();
  }

  function isWeekend(day) {
    return weekday(day) === 0 || weekday(day) === 6;
  }

  function nominalShipmentDay(timestamp) {
    const parts = Object.fromEntries(torontoDate.formatToParts(timestamp).map(({ type, value }) => [type, value]));
    const today = utcDay(Number(parts.year), Number(parts.month), Number(parts.day));
    const currentWeekday = weekday(today);
    const daysToMonday = currentWeekday === 0 ? (Number(parts.hour) < 3 ? 1 : 8) : 8 - currentWeekday;
    return today + daysToMonday;
  }

  function readConfig() {
    const text = document.getElementById('jomo-shipment-schedule-config')?.textContent || '';
    if (config && text === configText) return config;
    configText = text;
    let values;
    try { values = JSON.parse(text); } catch (_error) { values = {}; }
    const closedDates = new Set();
    for (const value of String(values?.closedDates || '').split(/[\s,;]+/)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) continue;
      const parsed = new Date(`${value}T00:00:00.000Z`);
      if (Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value) closedDates.add(value);
    }
    let formatter;
    const dateOptions = { timeZone: 'UTC', calendar: 'gregory', weekday: 'long', month: 'long', day: 'numeric' };
    try {
      formatter = new Intl.DateTimeFormat(values?.locale || 'en-CA', dateOptions);
    } catch (_error) {
      formatter = new Intl.DateTimeFormat('en-CA', dateOptions);
    }
    config = {
      closedDates, formatter,
      messageTemplate: typeof values?.messageTemplate === 'string' && values.messageTemplate
        ? values.messageTemplate : 'Next dispatch date: {{ date }}'
    };
    return config;
  }

  function nthMonday(year, month, occurrence) {
    const first = utcDay(year, month, 1);
    return first + ((8 - weekday(first)) % 7) + (occurrence - 1) * 7;
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const value = h + l - 7 * m + 114;
    return utcDay(year, Math.floor(value / 31), value % 31 + 1);
  }

  function holidaysForYear(year) {
    if (holidayYears.has(year)) return holidayYears.get(year);
    const easter = easterSunday(year);
    const may24 = utcDay(year, 5, 24);
    const fixed = [
      utcDay(year, 1, 1), utcDay(year, 7, 1), utcDay(year, 9, 30),
      utcDay(year, 11, 11), utcDay(year, 12, 25), utcDay(year, 12, 26)
    ];
    const holidays = new Set([
      ...fixed, nthMonday(year, 2, 3), easter - 2, easter + 1,
      may24 - ((weekday(may24) + 6) % 7), nthMonday(year, 8, 1),
      nthMonday(year, 9, 1), nthMonday(year, 10, 2)
    ]);
    for (const day of fixed) {
      if (!isWeekend(day)) continue;
      let observed = day + 1;
      while (isWeekend(observed) || holidays.has(observed)) observed++;
      holidays.add(observed);
    }
    holidayYears.set(year, holidays);
    return holidays;
  }

  function isHoliday(day) {
    const year = new Date(day * DAY).getUTCFullYear();
    return [year - 1, year, year + 1].some((candidate) => holidaysForYear(candidate).has(day));
  }

  function getNextShipmentDate(now = new Date()) {
    const timestamp = new Date(now).getTime();
    if (!Number.isFinite(timestamp)) return null;
    const { closedDates } = readConfig();
    let shipment = nominalShipmentDay(timestamp);
    for (let attempt = 0; attempt < 366; attempt++, shipment++) {
      if (!isWeekend(shipment) && !isHoliday(shipment) && !closedDates.has(dateKey(shipment))) return dateKey(shipment);
    }
    return null;
  }

  function formatMessage(date) {
    if (!date) return '';
    const { formatter, messageTemplate } = readConfig();
    return messageTemplate.replace(/__SHIPMENT_DATE__|\{\{\s*date\s*\}\}/g, formatter.format(new Date(`${date}T00:00:00.000Z`)));
  }

  function getMessage(now = new Date()) {
    return formatMessage(getNextShipmentDate(now));
  }

  function nextShipmentCutoff(timestamp) {
    const shipment = nominalShipmentDay(timestamp);
    let low = Math.floor(timestamp) + 1;
    let high = low + 8 * DAY;
    while (low < high) {
      const midpoint = Math.floor((low + high) / 2);
      if (nominalShipmentDay(midpoint) === shipment) low = midpoint + 1;
      else high = midpoint;
    }
    return low;
  }

  function refresh() {
    clearTimeout(refreshTimer);
    const now = Date.now();
    const date = getNextShipmentDate(now);
    const message = formatMessage(date);
    const signature = `${date || ''}\n${message}`;
    if (signature !== lastSignature) {
      lastSignature = signature;
      window.dispatchEvent(new CustomEvent('jomo:shipment-schedule-change', { detail: { date, message } }));
    }
    if (document.visibilityState !== 'hidden') {
      refreshTimer = setTimeout(refresh, nextShipmentCutoff(now) - now + 50);
    }
  }

  window.JomoShipmentSchedule = { getNextShipmentDate, getMessage, refresh };
  window.addEventListener('focus', refresh);
  window.addEventListener('pageshow', refresh);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') clearTimeout(refreshTimer);
    else refresh();
  });
  refresh();
})();
